import { useEffect, useMemo, useState } from 'react';
import * as Sentry from '@sentry/react';
import { usePortalIds } from '@shared/config';
import { Checkbox } from '@shared/components/ui/checkbox';
import { Label } from '@shared/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select';
import { Separator } from '@shared/components/ui/separator';

import { ScrollArea } from '@shared/components/ui/scroll-area';
import { Skeleton } from '@shared/components/ui/skeleton';
import { Calendar as CalendarIcon, Building, Gavel, BookOpen, ChevronRight, ChevronDown, AlertCircle } from 'lucide-react';
import { Button } from '@shared/components/ui/button';
import { Calendar } from '@shared/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@shared/components/ui/popover';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@shared/components/ui/collapsible';
import { Alert, AlertDescription } from '@shared/components/ui/alert';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { useSearchStore } from '@shared/stores/searchStore';
import { useDebounce } from '@shared/hooks/useDebounce';
import { SEARCH_CONFIG } from '@shared/lib/config/searchConfig';
import { useFilterCounts } from '@shared/hooks/useFilterCounts';
import { ThemaFilter } from '@shared/components/search/filters';

interface SearchFiltersProps {
  searchQuery?: string;
  selectedDomains: string[];
  selectedCourts: string[];
  selectedThemas: string[];
  dateRange: string;
  caseType: string;
  selectedWetsartikelen: string[];
  customDateRange?: { from: Date | undefined; to: Date | undefined };
  onCourtToggle: (courtId: string, courtName?: string) => void;
  onCourtsChange?: (courtIds: string[], courtNames?: Record<string, string>) => void;
  onThemaToggle: (themaId: string, themaName?: string) => void;
  onThemasChange?: (themaIds: string[], themaNames?: Record<string, string>) => void;
  onDateRangeChange: (value: string) => void;
  onCustomDateRangeChange?: (range: { from: Date | undefined; to: Date | undefined }) => void;
  onCaseTypeChange: (value: string, typeName?: string) => void;
  onWetsartikelToggle: (artikelId: string, artikelReferentie?: string) => void;
  onWetsartikelenChange?: (artikelIds: string[], artikelNames?: Record<string, string>) => void;
  isMobile?: boolean;
}

// Types are now imported from useFilterCounts hook

/**
 * Skeleton placeholder for filter list loading state
 */
function FilterListSkeleton({ count = 5 }: { count?: number }) {
  return (
    <div className="space-y-2 py-2">
      {[...Array(count)].map((_, i) => (
        <div key={i} className="flex items-center space-x-2">
          <Skeleton className="h-4 w-4 rounded" />
          <Skeleton className="h-4 flex-1" />
          <Skeleton className="h-3 w-8" />
        </div>
      ))}
    </div>
  );
}

function SearchFiltersComponent({
  searchQuery,
  selectedDomains,
  selectedCourts,
  selectedThemas,
  dateRange,
  caseType,
  selectedWetsartikelen,
  customDateRange,
  onCourtToggle,
  onCourtsChange,
  onThemaToggle,
  onThemasChange,
  onDateRangeChange,
  onCustomDateRangeChange,
  onCaseTypeChange,
  onWetsartikelToggle,
  onWetsartikelenChange,
  isMobile = false,
}: SearchFiltersProps) {
  // Get portal IDs from context for portal-specific filter counts
  // This array may include multiple portals (e.g., lexzone + lexwoo during validation)
  const portalIds = usePortalIds();

  const [fromDateOpen, setFromDateOpen] = useState(false);
  const [toDateOpen, setToDateOpen] = useState(false);
  const [localDateRange, setLocalDateRange] = useState<{ from: Date | undefined; to: Date | undefined }>({
    from: customDateRange?.from,
    to: customDateRange?.to
  });
  // Sync localDateRange when customDateRange prop changes externally (e.g. clearFilters)
  useEffect(() => {
    setLocalDateRange({
      from: customDateRange?.from,
      to: customDateRange?.to,
    });
  }, [customDateRange?.from, customDateRange?.to]);
  // Get expanded state and toggle actions from Zustand store
  // Using individual selectors prevents re-renders when unrelated state changes
  // Note: expandedThemas and toggleThema are handled by ThemaFilter component
  const expandedSections = useSearchStore((state) => state.uiState.expandedSections);
  const expandedWetboeken = useSearchStore((state) => state.uiState.expandedWetboeken);
  const expandedArtikelen = useSearchStore((state) => state.uiState.expandedArtikelen);
  const expandedInstantieNiveaus = useSearchStore((state) => state.uiState.expandedInstantieNiveaus);
  const toggleSection = useSearchStore((state) => state.toggleSection);
  const toggleWetboek = useSearchStore((state) => state.toggleWetboek);
  const toggleArtikel = useSearchStore((state) => state.toggleArtikel);
  const toggleInstantieNiveau = useSearchStore((state) => state.toggleInstantieNiveau);

  // Use longer debounce for search query to reduce filter count refetches while typing
  const debouncedSearchQuery = useDebounce(searchQuery, SEARCH_CONFIG.FILTER_COUNTS_DEBOUNCE_MS);

  // Memoize date range calculation to prevent unnecessary re-renders
  const { dateFrom, dateTo } = useMemo(() => {
    let dateFrom: string | undefined;
    let dateTo: string | undefined;
    const today = new Date();

    switch (dateRange) {
      case 'week':
        dateFrom = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        break;
      case 'month':
        dateFrom = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        break;
      case 'quarter':
        dateFrom = new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        break;
      case 'year':
        dateFrom = new Date(today.getTime() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        break;
      case 'custom':
        if (customDateRange?.from instanceof Date && !isNaN(customDateRange.from.getTime())) {
          dateFrom = customDateRange.from.toISOString().split('T')[0];
        }
        if (customDateRange?.to instanceof Date && !isNaN(customDateRange.to.getTime())) {
          dateTo = customDateRange.to.toISOString().split('T')[0];
        }
        break;
    }

    return { dateFrom, dateTo };
  }, [dateRange, customDateRange?.from?.getTime(), customDateRange?.to?.getTime()]);

  // Use React Query for filter counts - provides caching, deduplication, and stale-while-revalidate
  const {
    data: filterData,
    isLoading,
    error,
    refetch,
  } = useFilterCounts({
    searchQuery: debouncedSearchQuery || undefined,
    selectedDomains: selectedDomains.length > 0 ? selectedDomains : undefined,
    selectedCourts: selectedCourts.length > 0 ? selectedCourts : undefined,
    selectedThemas: selectedThemas.length > 0 ? selectedThemas : undefined,
    selectedWetsartikelen: selectedWetsartikelen.length > 0 ? selectedWetsartikelen : undefined,
    caseType: caseType !== 'all' ? caseType : undefined,
    dateFrom,
    dateTo,
    portalIds,
  });

  // Extract data from React Query result with defaults
  const instantieNiveauHierarchy = filterData?.instantieHierarchy ?? [];
  const caseTypes = filterData?.caseTypes ?? [];
  const themaHierarchy = filterData?.themaHierarchy ?? [];
  const wetsartikelHierarchy = filterData?.wetsartikelHierarchy ?? [];

  return (
    <div className="space-y-6">
      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error instanceof Error ? error.message : 'Fout bij laden van filters'}
            <Button
              variant="link"
              size="sm"
              className="ml-2 h-auto p-0 text-xs underline"
              onClick={() => refetch()}
            >
              Opnieuw proberen
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Date Range */}
      <Collapsible
        open={expandedSections.includes('periode')}
        onOpenChange={() => toggleSection('periode')}
      >
        <div className="space-y-3">
          <CollapsibleTrigger asChild>
            <button className="w-full flex items-center justify-between hover:bg-accent/50 rounded px-2 py-1 transition-colors">
              <Label className="text-sm font-medium flex items-center gap-2 cursor-pointer">
                <CalendarIcon className="h-4 w-4" />
                Periode
              </Label>
              {expandedSections.includes('periode') ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="space-y-3 px-2 mt-3 pb-2">
        <Select value={dateRange} onValueChange={onDateRangeChange}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Selecteer periode" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle data</SelectItem>
            <SelectItem value="week">Afgelopen week</SelectItem>
            <SelectItem value="month">Afgelopen maand</SelectItem>
            <SelectItem value="quarter">Afgelopen kwartaal</SelectItem>
            <SelectItem value="year">Afgelopen jaar</SelectItem>
            <SelectItem value="custom">Aangepaste periode</SelectItem>
          </SelectContent>
        </Select>
        
        {/* Custom Date Range Picker */}
        {dateRange === 'custom' && (
          <div className="space-y-2">
            {/* From Date Picker */}
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">Van</Label>
              <Popover open={fromDateOpen} onOpenChange={setFromDateOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-between font-normal"
                  >
                    {localDateRange?.from ? (
                      format(localDateRange.from, "d MMM yyyy", { locale: nl })
                    ) : (
                      "Selecteer datum"
                    )}
                    <CalendarIcon className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={localDateRange.from}
                    defaultMonth={localDateRange.from || new Date()}
                    onSelect={(date) => {
                      const newRange = {
                        ...localDateRange,
                        from: date
                      };
                      setLocalDateRange(newRange);
                      if (onCustomDateRangeChange) {
                        onCustomDateRangeChange(newRange);
                      }
                      setFromDateOpen(false);
                    }}
                    locale={nl}
                    captionLayout="dropdown"
                    fromYear={2000}
                    toYear={new Date().getFullYear()}
                    disabled={(date) => {
                      // Disable future dates
                      const today = new Date();
                      today.setHours(23, 59, 59, 999);
                      if (date > today) {
                        return true;
                      }
                      // Disable dates after 'to' date if it exists
                      if (localDateRange.to) {
                        return date > localDateRange.to;
                      }
                      return false;
                    }}
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* To Date Picker */}
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">Tot</Label>
              <Popover open={toDateOpen} onOpenChange={setToDateOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-between font-normal"
                  >
                    {localDateRange?.to ? (
                      format(localDateRange.to, "d MMM yyyy", { locale: nl })
                    ) : (
                      "Selecteer datum"
                    )}
                    <CalendarIcon className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={localDateRange.to}
                    defaultMonth={localDateRange.to || localDateRange.from || new Date()}
                    onSelect={(date) => {
                      const newRange = {
                        ...localDateRange,
                        to: date
                      };
                      setLocalDateRange(newRange);
                      if (onCustomDateRangeChange) {
                        onCustomDateRangeChange(newRange);
                      }
                      setToDateOpen(false);
                    }}
                    locale={nl}
                    captionLayout="dropdown"
                    fromYear={2000}
                    toYear={new Date().getFullYear()}
                    disabled={(date) => {
                      // Disable future dates
                      const today = new Date();
                      today.setHours(23, 59, 59, 999);
                      if (date > today) {
                        return true;
                      }
                      // Disable dates before 'from' date if it exists
                      if (localDateRange.from) {
                        const fromDate = new Date(localDateRange.from);
                        fromDate.setHours(0, 0, 0, 0);
                        const checkDate = new Date(date);
                        checkDate.setHours(0, 0, 0, 0);
                        return checkDate < fromDate;
                      }
                      return false;
                    }}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
        )}
            </div>
          </CollapsibleContent>
        </div>
      </Collapsible>

            <Separator />

      {/* Themas - extracted to ThemaFilter component */}
      <ThemaFilter
        themaHierarchy={themaHierarchy}
        selectedThemas={selectedThemas}
        onToggle={onThemaToggle}
        onBulkChange={onThemasChange}
        isLoading={isLoading}
        isMobile={isMobile}
      />

      {/* Wetsartikelen */}
      <Collapsible
        open={expandedSections.includes('wetsartikelen')}
        onOpenChange={() => toggleSection('wetsartikelen')}
      >
        <div className="space-y-3">
          <CollapsibleTrigger asChild>
            <button className="w-full flex items-center justify-between hover:bg-accent/50 rounded px-2 py-1 transition-colors">
              <Label className="text-sm font-medium flex items-center gap-2 cursor-pointer">
                <BookOpen className="h-4 w-4" />
                Wetsartikelen
              </Label>
              {expandedSections.includes('wetsartikelen') ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="px-2 mt-3 pb-2">
              <ScrollArea className="max-h-48">
                <div className="space-y-1">
            {isLoading ? (
              <FilterListSkeleton />
            ) : wetsartikelHierarchy.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                Geen wetsartikelen beschikbaar
              </p>
            ) : (
              wetsartikelHierarchy.map(wetboek => {
                const wetboekKey = wetboek.wetboek_afkorting;
                const isWetboekExpanded = expandedWetboeken.includes(wetboekKey);

                // Get all artikel IDs under this wetboek
                const allArtikelIds = wetboek.artikelen.flatMap(a => a.items.map(i => i.id));
                const allSelected = allArtikelIds.every(id => selectedWetsartikelen.includes(id));
                const someSelected = allArtikelIds.some(id => selectedWetsartikelen.includes(id));

                return (
                  <Collapsible
                    key={wetboekKey}
                    open={isWetboekExpanded}
                    onOpenChange={() => toggleWetboek(wetboekKey)}
                  >
                    <div className="flex items-center space-x-2 py-1">
                      <Checkbox
                        id={`${isMobile ? 'mobile-' : ''}wetboek-${wetboekKey}`}
                        checked={allSelected ? true : someSelected ? "indeterminate" : false}
                        onCheckedChange={(checked) => {
                          // Select or deselect all artikelen in this wetboek
                          if (onWetsartikelenChange) {
                            // Use bulk update if available
                            const newSelection = checked
                              ? [...new Set([...selectedWetsartikelen, ...allArtikelIds])]
                              : selectedWetsartikelen.filter(id => !allArtikelIds.includes(id));

                            // Build name mapping for new selections
                            const nameMapping: Record<string, string> = {};
                            if (checked) {
                              allArtikelIds.forEach(id => {
                                const artikel = wetboek.artikelen
                                  .flatMap(a => a.items)
                                  .find(i => i.id === id);
                                if (artikel) {
                                  nameMapping[id] = artikel.label;
                                }
                              });
                            }

                            onWetsartikelenChange(newSelection, nameMapping);
                          } else {
                            // Fall back to individual toggles
                            const idsToToggle = checked
                              ? allArtikelIds.filter(id => !selectedWetsartikelen.includes(id))
                              : allArtikelIds.filter(id => selectedWetsartikelen.includes(id));

                            idsToToggle.forEach(id => {
                              const artikel = wetboek.artikelen
                                .flatMap(a => a.items)
                                .find(i => i.id === id);
                              setTimeout(() => onWetsartikelToggle(id, artikel?.label), 0);
                            });
                          }
                        }}
                      />
                      <CollapsibleTrigger asChild>
                        <button className="flex items-center flex-1 text-left hover:bg-accent/50 rounded px-1 py-0.5 transition-colors">
                          {isWetboekExpanded ? (
                            <ChevronDown className="h-3 w-3 shrink-0 mr-1" />
                          ) : (
                            <ChevronRight className="h-3 w-3 shrink-0 mr-1" />
                          )}
                          <span className="text-sm font-medium">{wetboek.wetboek_afkorting}</span>
                          <span className="text-muted-foreground text-xs ml-auto">({wetboek.totalCount})</span>
                        </button>
                      </CollapsibleTrigger>
                    </div>

                    <CollapsibleContent className="ml-4 space-y-1 mt-1">
                      {wetboek.artikelen.map(artikel => {
                        const artikelKey = `${wetboekKey}-${artikel.artikel_nummer}`;
                        const isArtikelExpanded = expandedArtikelen.includes(artikelKey);

                        // Get all item IDs under this artikel
                        const artikelItemIds = artikel.items.map(i => i.id);
                        const allArtikelSelected = artikelItemIds.every(id => selectedWetsartikelen.includes(id));
                        const someArtikelSelected = artikelItemIds.some(id => selectedWetsartikelen.includes(id));

                        return (
                          <Collapsible
                            key={artikelKey}
                            open={isArtikelExpanded}
                            onOpenChange={() => toggleArtikel(artikelKey)}
                          >
                            <div className="flex items-center space-x-2 py-1">
                              <Checkbox
                                id={`${isMobile ? 'mobile-' : ''}artikel-${artikelKey}`}
                                checked={allArtikelSelected ? true : someArtikelSelected ? "indeterminate" : false}
                                onCheckedChange={(checked) => {
                                  // Select or deselect all items in this artikel
                                  if (onWetsartikelenChange) {
                                    // Use bulk update if available
                                    const newSelection = checked
                                      ? [...new Set([...selectedWetsartikelen, ...artikelItemIds])]
                                      : selectedWetsartikelen.filter(id => !artikelItemIds.includes(id));

                                    // Build name mapping for new selections
                                    const nameMapping: Record<string, string> = {};
                                    if (checked) {
                                      artikelItemIds.forEach(id => {
                                        const item = artikel.items.find(i => i.id === id);
                                        if (item) {
                                          nameMapping[id] = item.label;
                                        }
                                      });
                                    }

                                    onWetsartikelenChange(newSelection, nameMapping);
                                  } else {
                                    // Fall back to individual toggles
                                    const idsToToggle = checked
                                      ? artikelItemIds.filter(id => !selectedWetsartikelen.includes(id))
                                      : artikelItemIds.filter(id => selectedWetsartikelen.includes(id));

                                    idsToToggle.forEach(id => {
                                      const item = artikel.items.find(i => i.id === id);
                                      setTimeout(() => onWetsartikelToggle(id, item?.label), 0);
                                    });
                                  }
                                }}
                              />
                              <CollapsibleTrigger asChild>
                                <button className="flex items-center flex-1 text-left hover:bg-accent/50 rounded px-1 py-0.5 transition-colors">
                                  {isArtikelExpanded ? (
                                    <ChevronDown className="h-3 w-3 shrink-0 mr-1" />
                                  ) : (
                                    <ChevronRight className="h-3 w-3 shrink-0 mr-1" />
                                  )}
                                  <span className="text-sm">Art. {artikel.artikel_nummer}</span>
                                  <span className="text-muted-foreground text-xs ml-auto">({artikel.totalCount})</span>
                                </button>
                              </CollapsibleTrigger>
                            </div>

                            <CollapsibleContent className="ml-4 space-y-1 mt-1">
                              {artikel.items.map(item => (
                                <div
                                  key={`${artikelKey}-${item.id}`}
                                  className="flex items-center space-x-2 py-1"
                                >
                                  <Checkbox
                                    id={`${isMobile ? 'mobile-' : ''}item-${item.id}`}
                                    checked={selectedWetsartikelen.includes(item.id)}
                                    onCheckedChange={() => onWetsartikelToggle(item.id, item.label)}
                                  />
                                  <Label
                                    htmlFor={`${isMobile ? 'mobile-' : ''}item-${item.id}`}
                                    className="text-xs font-normal flex-1 flex justify-between items-center cursor-pointer"
                                  >
                                    <span className="truncate" title={item.label}>{item.label}</span>
                                    <span className="text-muted-foreground text-xs ml-2 shrink-0">({item.count})</span>
                                  </Label>
                                </div>
                              ))}
                            </CollapsibleContent>
                          </Collapsible>
                        );
                      })}
                    </CollapsibleContent>
                  </Collapsible>
                );
              })
            )}
                </div>
              </ScrollArea>
            </div>
          </CollapsibleContent>
        </div>
      </Collapsible>

      <Separator />

      {/* Courts (Instanties) */}
      <Collapsible
        open={expandedSections.includes('instanties')}
        onOpenChange={() => toggleSection('instanties')}
      >
        <div className="space-y-3">
          <CollapsibleTrigger asChild>
            <button className="w-full flex items-center justify-between hover:bg-accent/50 rounded px-2 py-1 transition-colors">
              <Label className="text-sm font-medium flex items-center gap-2 cursor-pointer">
                <Building className="h-4 w-4" />
                Instanties
              </Label>
              {expandedSections.includes('instanties') ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="px-2 mt-3 pb-2">
              <ScrollArea className="max-h-48">
                <div className="space-y-1">
            {isLoading ? (
              <FilterListSkeleton />
            ) : instantieNiveauHierarchy.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                Geen instanties beschikbaar
              </p>
            ) : (
              instantieNiveauHierarchy.map(niveauGroup => {
                const niveauKey = niveauGroup.niveau;
                const isNiveauExpanded = expandedInstantieNiveaus?.includes(niveauKey) ?? false;

                // Only consider visible (non-zero count) instanties for checkbox state
                const visibleInstanties = niveauGroup.instanties.filter(i => i.count > 0);
                const allInstantieIds = visibleInstanties.map(i => i.id);
                const allSelected = allInstantieIds.length > 0 && allInstantieIds.every(id => selectedCourts.includes(id));
                const someSelected = allInstantieIds.some(id => selectedCourts.includes(id));

                return (
                  <Collapsible
                    key={niveauKey}
                    open={isNiveauExpanded}
                    onOpenChange={() => toggleInstantieNiveau(niveauKey)}
                  >
                    <div className="flex items-center space-x-2 py-1">
                      <Checkbox
                        id={`${isMobile ? 'mobile-' : ''}niveau-${niveauKey}`}
                        checked={allSelected ? true : someSelected ? "indeterminate" : false}
                        onCheckedChange={(checked) => {
                          // Select or deselect all instanties in this niveau
                          if (onCourtsChange) {
                            // Use bulk update if available
                            const newSelection = checked
                              ? [...new Set([...selectedCourts, ...allInstantieIds])]
                              : selectedCourts.filter(id => !allInstantieIds.includes(id));

                            // Build name mapping for new selections
                            const nameMapping: Record<string, string> = {};
                            if (checked) {
                              visibleInstanties.forEach(instantie => {
                                nameMapping[instantie.id] = instantie.naam;
                              });
                            }

                            onCourtsChange(newSelection, nameMapping);
                          } else {
                            // Fall back to individual toggles
                            const idsToToggle = checked
                              ? allInstantieIds.filter(id => !selectedCourts.includes(id))
                              : allInstantieIds.filter(id => selectedCourts.includes(id));

                            idsToToggle.forEach(id => {
                              const instantie = niveauGroup.instanties.find(i => i.id === id);
                              if (!instantie) return; // Defensive check - should never happen
                              setTimeout(() => onCourtToggle(id, instantie.naam), 0);
                            });
                          }
                        }}
                      />
                      <CollapsibleTrigger asChild>
                        <button className="flex items-center flex-1 text-left hover:bg-accent/50 rounded px-1 py-0.5 transition-colors">
                          {isNiveauExpanded ? (
                            <ChevronDown className="h-3 w-3 shrink-0 mr-1" />
                          ) : (
                            <ChevronRight className="h-3 w-3 shrink-0 mr-1" />
                          )}
                          <span className="text-sm font-medium">{niveauGroup.naam}</span>
                          <span className="text-muted-foreground text-xs ml-auto">({niveauGroup.totalCount})</span>
                        </button>
                      </CollapsibleTrigger>
                    </div>

                    <CollapsibleContent className="ml-4 space-y-1 mt-1">
                      {niveauGroup.instanties.filter(instantie => instantie.count > 0).map(instantie => (
                        <div
                          key={`${niveauKey}-${instantie.id}`}
                          className="flex items-center space-x-2 py-1"
                        >
                          <Checkbox
                            id={`${isMobile ? 'mobile-' : ''}court-${instantie.id}`}
                            checked={selectedCourts.includes(instantie.id)}
                            onCheckedChange={() => onCourtToggle(instantie.id, instantie.naam)}
                          />
                          <Label
                            htmlFor={`${isMobile ? 'mobile-' : ''}court-${instantie.id}`}
                            className="text-sm font-normal flex-1 flex justify-between items-center cursor-pointer"
                          >
                            <span className="truncate" title={instantie.naam}>{instantie.naam}</span>
                            <span className="text-muted-foreground text-xs ml-2 shrink-0">({instantie.count})</span>
                          </Label>
                        </div>
                      ))}
                    </CollapsibleContent>
                  </Collapsible>
                );
              })
            )}
                </div>
              </ScrollArea>
            </div>
          </CollapsibleContent>
        </div>
      </Collapsible>

      <Separator />

      {/* Case Type */}
      <Collapsible
        open={expandedSections.includes('type')}
        onOpenChange={() => toggleSection('type')}
      >
        <div className="space-y-3">
          <CollapsibleTrigger asChild>
            <button className="w-full flex items-center justify-between hover:bg-accent/50 rounded px-2 py-1 transition-colors">
              <Label className="text-sm font-medium flex items-center gap-2 cursor-pointer">
                <Gavel className="h-4 w-4" />
                Type uitspraak
              </Label>
              {expandedSections.includes('type') ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="space-y-3 px-2 mt-3 pb-2">
        <Select value={caseType} onValueChange={(value) => {
          const type = caseTypes.find(t => t.id === value);
          onCaseTypeChange(value, type?.label);
        }} disabled={isLoading}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Selecteer type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle types</SelectItem>
            {caseTypes.map(type => (
              <SelectItem key={type.id} value={type.id}>
                {type.label} ({type.count})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
            </div>
          </CollapsibleContent>
        </div>
      </Collapsible>



    </div>
  );
}

// Export with Sentry profiler for performance monitoring
export const SearchFilters = Sentry.withProfiler(SearchFiltersComponent, { name: 'SearchFilters' });